// @flow
import React, { Component } from 'react';
import moment from 'moment';
import { compose } from 'recompose';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/styles';
import { CircularProgress, Fade } from '@material-ui/core';
import {
    Check as CheckIcon,
    Clear as ClearIcon,
    DateRange as DateRangeIcon,
    KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@material-ui/icons';
import type { Dispatch } from 'redux';
import type { FormProps } from 'redux-form';
import type { Connector } from 'react-redux';
import type { ContextRouter } from 'react-router';
import type { TFunction } from 'react-i18next';
import fieldify from '../../../../../../components/fieldify';
import DateTimePicker from '../../../../../../components/DateTimePicker';
import { actions, selectors } from './duck';
import styles from './styles';
import type { State as RootState } from '../../../../../../redux/initialState';

type State = {
    statusTimer: ?number,
};

type OwnProps = ContextRouter & FormProps & {
    classes: Object,
    className?: string,
    t: TFunction,
};
type StateProps = {};
type Props = OwnProps & StateProps;

const DateTimePickerField = fieldify()(DateTimePicker);
const ReadOnlyField = fieldify()(
    (props: Object) => {
        const { className, value } = props;
        return <div className={className}>{value}</div>;
    }
);

const formatDate = (value) => (value ? moment(value).format('M/D/YYYY h:mma') : 'N/A');

export class Timeline extends Component<Props, State> {
    state = {
        statusTimer: null,
    };

    componentWillReceiveProps(nextProps: Props) {
        const { submitFailed, submitSucceeded } = this.props;
        const displayStatus = (!submitFailed && nextProps.submitFailed)
            || (!submitSucceeded && nextProps.submitSucceeded);

        if (displayStatus) {
            const { statusTimer } = this.state;
            clearTimeout(statusTimer);

            this.setState({
                statusTimer: setTimeout(() => this.setState({ statusTimer: null }), 1500),
            });
        }
    }

    handleBlur = () => {
        const { dirty, handleSubmit, valid } = this.props;
        if (dirty && valid) {
            handleSubmit();
        }
    };

    renderStartDate() {
        const { classes, disabled, readOnly } = this.props;
        return readOnly.includes('startDate')
            ? (
                <Field
                  name="startDate"
                  format={formatDate}
                  component={ReadOnlyField}
                  className={classes.readOnly}
                />
            )
            : (
                <Field
                  name="startDate"
                  component={DateTimePickerField}
                  disabled={disabled.includes('startDate')}
                  classes={{ dateInput: classes.dateInput }}
                  onBlur={this.handleBlur}
                />
            );
    }

    renderDueDate() {
        const { classes, disabled, readOnly } = this.props;
        return readOnly.includes('dueDate')
            ? (
                <Field
                  name="dueDate"
                  format={formatDate}
                  component={ReadOnlyField}
                  className={classes.readOnly}
                />
            )
            : (
                <Field
                  name="dueDate"
                  component={DateTimePickerField}
                  disabled={disabled.includes('dueDate')}
                  classes={{ dateInput: classes.dateInput }}
                  onBlur={this.handleBlur}
                />
            );
    }

    render() {
        const { classes, handleSubmit, submitSucceeded, submitting, t } = this.props;
        const { statusTimer } = this.state;
        return (
            <form className={classes.container} onSubmit={handleSubmit}>
                <DateRangeIcon className={classes.icon} />
                <span className={classes.label}>{t('ticketDetail.summary.timeline')}</span>
                {this.renderStartDate()}
                <KeyboardArrowRightIcon className={classes.rightArrowIcon} />
                {this.renderDueDate()}
                {submitting
                    ? <CircularProgress size={18} thickness={4} />
                    : (
                        <Fade in={!!statusTimer} timeout={{ exit: 250 }}>
                            {submitSucceeded
                                ? <CheckIcon className={classes.successIcon} />
                                : <ClearIcon className={classes.errorIcon} />
                            }
                        </Fade>
                    )
                }
            </form>
        );
    }
}

const mapStateToProps = (state: RootState, props: OwnProps): StateProps => ({
    disabled: selectors.getDisabled(state, props),
    initialValues: selectors.getInitialValues(state, props),
    readOnly: selectors.getReadOnly(state, props),
});

const connector: Connector<OwnProps, Props> = connect(mapStateToProps);
const enhance = compose(
    withStyles(styles, { name: 'Timeline' }),
    withTranslation(),
    connector,
    reduxForm({
        form: 'timeline',
        enableReinitialize: true,
        destroyOnUnmount: false,
        onSubmit: (values: Object, dispatch: Dispatch<any>, props: Props): Promise<any> => dispatch(actions.submit(values, props)),
    })
);

export default enhance(Timeline);
